import round from 'lodash/round'

/**
 * The one converter between a persisted `RenderConfig.clipScene` window and the
 * numbers a Remotion clip composition needs.
 *
 * ## The two-frame rule
 *
 * There are exactly two frames of reference in play, and #72 was what happens
 * when they get mixed:
 *
 * - **Recording-relative** — seconds from the start of the *recording*. This is
 *   what `clipScene.start`/`clipScene.end` are persisted as (`clipSceneFor`
 *   writes the selection straight off the recording timeline), and what every
 *   transcript utterance time is in.
 * - **Media-relative** — seconds into the file the composition actually
 *   plays. `mediaStart` is `trimBefore` seconds into *that* file
 *   (`ClipScene.tsx`, `VideoLayout.tsx`). A render always plays the recording
 *   (#377 retired clip files), so today the two frames coincide — which is exactly when a mix-up hides, so keep them
 *   apart anyway.
 *
 * #72 was a render that read `clipScene.start` as clip-local and computed the
 * offset twice, in two places, so the caption offset drifted apart from the
 * media offset. Callers take **one** result from here and thread it to both.
 *
 * ## Caption offset
 *
 * `referenceStart` is `absoluteStart`: utterance times are recording-relative,
 * and every transcript is recording-scoped.
 *
 * ## Segments: the third timeline (#89)
 *
 * A render plays only the *kept* parts of its window — the window minus
 * skipped words and `skipSegments` (`util/skipRanges.ts`). The v1 `createRender` route
 * (retired in #546) computed them once and persisted them as `clipScene.segments`
 * (recording-relative), so every chunk reads the same frozen timeline. That
 * adds a third frame of reference, the **output** timeline the composition
 * runs on:
 *
 * ```
 *   recording  0 ──── 100 ▓▓▓ 103 ─────── 110 ▓▓ 112 ──── 120 ──▶
 *                     │ seg 0 │             │ seg 1 │
 *   output            0 ─────────── 7  7 ─────────────── 15
 *   media (rec file)  100 ──────── 103 ║ 103→110 cut ║ 112 ─────── 120
 * ```
 *
 * Each segment carries its own `mediaStart` and its `outputStart`.
 * Captions go through `toOutputTime`, which answers `null` inside a cut.
 *
 * Without segments (every render created before #89, and any retry of one) the
 * whole window is one segment, and every value below is what it was before.
 */

/**
 * The shortest scene we will describe, in seconds: one frame at 30 fps.
 *
 * Sized to exactly one frame on purpose — see the `effectiveDuration` invariant
 * below. `clipSceneDurationInFrames` floors the composition at 1 frame anyway,
 * so anything larger would only buy the chance of overshooting `Render.duration`
 * for a sub-frame selection.
 */
export const MIN_SCENE_SECONDS = 1 / 30

/** The window a render describes, and where it sits in the media. */
export type ClipSceneTiming = {
  /** Window start, recording-relative. */
  absoluteStart: number
  /** Window end, recording-relative. Never less than `absoluteStart`. */
  absoluteEnd: number
  /**
   * What recording-relative transcript times are measured against. Always
   * `absoluteStart` — see the caption-offset note above.
   */
  referenceStart: number
  /** `trimBefore` seconds into the recording's file. */
  mediaStart: number
  /**
   * Scene length in seconds, reconciled with both the window and the persisted
   * duration. Replaces the raw `clipScene.duration` everywhere downstream
   * (composition length, frameRange, trimAfter).
   *
   * **Invariant: never longer than the persisted `duration`.** `Render.duration`
   * is a copy of that value (`createPaidRender`) and is what `createRenderChunks`
   * divides into chunks, so a composition longer than it would have no chunk
   * covering its tail — and the concatenated output would silently lose the
   * end. The other direction is safe: a chunk that overruns a shorter
   * composition is clamped by `chunkFrameRange`.
   *
   * The window still shortens it, which is the point — an unclamped
   * `end - start` can no longer outrun the media or the captions.
   */
  effectiveDuration: number
  /**
   * The kept pieces, in order, each placed on all three timelines. One piece
   * covering the window when the config carries no segments; empty when every
   * moment of the window is skipped (callers refuse to render that).
   */
  segments: TimedSegment[]
  /**
   * Recording time → output time, or `null` inside a cut or outside the
   * window. Both segment edges are inclusive, so a caption that ends exactly
   * at a cut still maps.
   */
  toOutputTime: (t: number) => number | null
}

export type TimedSegment = {
  /** Recording-relative. */
  start: number
  /** Recording-relative. */
  end: number
  /** `trimBefore` seconds into the recording's file. */
  mediaStart: number
  /** Where the segment begins in the composition, in seconds. */
  outputStart: number
}

type TimingConfig = {
  start: number
  end: number
  /**
   * The persisted `clipScene.duration`, when there is one. Omitted by the
   * caller that is computing the value to persist in the first place
   * (`clipSceneFor`), which is why it is optional.
   */
  duration?: number | undefined
  /**
   * The kept pieces of the window, recording-relative (`keptRanges`).
   * `null`/absent means "the whole window" — a render from before #89. An
   * empty array means nothing is kept.
   */
  segments?: ReadonlyArray<{ start: number; end: number }> | null | undefined
}

export function resolveClipSceneTiming({
  clipSceneConfig,
}: {
  clipSceneConfig: TimingConfig
}): ClipSceneTiming {
  const absoluteStart = round(clipSceneConfig.start, 3)
  const absoluteEnd = round(
    Math.max(clipSceneConfig.end, clipSceneConfig.start),
    3,
  )

  const ranges =
    clipSceneConfig.segments == null
      ? [{ start: absoluteStart, end: absoluteEnd }]
      : clipSceneConfig.segments
          .map((seg) => ({
            start: round(Math.max(seg.start, absoluteStart), 3),
            end: round(Math.min(seg.end, absoluteEnd), 3),
          }))
          .filter((seg) => seg.end > seg.start)

  // The window, or — with segments — only what is kept of it. Identical to
  // `absoluteEnd - absoluteStart` when there are none.
  const windowDuration = round(
    ranges.reduce((sum, seg) => sum + (seg.end - seg.start), 0),
    3,
  )

  const persistedDuration = clipSceneConfig.duration

  const effectiveDuration = Math.max(
    persistedDuration != null &&
      Number.isFinite(persistedDuration) &&
      persistedDuration > 0
      ? Math.min(windowDuration, persistedDuration)
      : windowDuration,
    MIN_SCENE_SECONDS,
  )

  let outputCursor = 0
  const segments: TimedSegment[] = ranges.map((seg) => {
    const timed = {
      start: seg.start,
      end: seg.end,
      mediaStart: seg.start,
      outputStart: round(outputCursor, 3),
    }
    outputCursor += seg.end - seg.start
    return timed
  })

  return {
    absoluteStart,
    absoluteEnd,
    referenceStart: absoluteStart,
    mediaStart: segments[0]?.start ?? absoluteStart,
    effectiveDuration,
    segments,
    toOutputTime: (t) => {
      for (const seg of segments) {
        if (t < seg.start) return null // ordered: t sits in a cut before seg
        if (t <= seg.end) return round(seg.outputStart + (t - seg.start), 3)
      }
      return null
    },
  }
}

/**
 * The segments as the template takes them — media-relative, with durations —
 * or `undefined` when there is at most one.
 *
 * Omitted for a single segment on purpose: a scene without `segments` plays
 * `[mediaStart, mediaStart + duration]` exactly as before #89, so a render
 * with no cuts sends byte-identical props, and a template bundle that predates
 * `segments` still plays it correctly.
 */
export function sceneSegments(
  timing: Pick<ClipSceneTiming, 'segments'>,
): Array<{ mediaStart: number; duration: number }> | undefined {
  if (timing.segments.length <= 1) return undefined
  return timing.segments.map((seg) => ({
    mediaStart: seg.mediaStart,
    duration: round(seg.end - seg.start, 3),
  }))
}

/**
 * Output time → recording time: where the composition's playhead is in the
 * recording. The inverse of `toOutputTime`, for the preview's transcript sync.
 * Past the last segment it stays at that segment's end.
 */
export function outputToRecordingTime(
  segments: readonly TimedSegment[],
  outputTime: number,
): number {
  let current = segments[0]
  if (!current) return outputTime
  for (const seg of segments) {
    if (seg.outputStart > outputTime) break
    current = seg
  }
  return Math.min(
    current.start + Math.max(0, outputTime - current.outputStart),
    current.end,
  )
}
